import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnimatedScrollHandler } from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import Amount from 'design-system/Amount';
import EmojiText from 'design-system/EmojiText';
import LogoBankAccount from 'design-system/LogoBankAccount';
import { ModalFlatList } from 'design-system/ModalScrollables';
import SectionHeader from 'design-system/SectionHeader';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks/useScrollableModalMaxHeight';
import { useConnectedAccount } from 'features/invest/hooks/selectors';
import TransferAccountRow from 'features/transfers/components/TransferAccountRow';
import { selectConnectedBankAccount } from 'features/wealth/selectors';
import Modal from 'utils/packages/Modal';
import { selectTransferPotsFromPotTypeWithAccInterestPots } from '../reducers/selectors';
const keyExtractor = (item) => item.id;
const strings = {
    title: 'Choose a deposit method',
    fastTransfers: 'Fast transfers',
    otherTransferType: 'Other transfer type',
    openBankingTransfer: 'Open Banking transfer',
    directDebit: 'Direct Debit',
    openBankingTiming: 'Usually instant',
    directDebitTiming: 'May take up to 5 working days',
};
const getAccountLabel = (bankName, accountNumber) => `${bankName || 'Linked bank'} • ${!accountNumber ? '****' : accountNumber.slice(-4)}`;
const ChooseAccountToDepositToPotModalContent = ({ pot, current, onSelect, sharedValue, }) => {
    const connectedAccount = useConnectedAccount();
    const connectedBankAccount = useAppSelector(selectConnectedBankAccount);
    const potsOfTheSameType = useAppSelector((state) => selectTransferPotsFromPotTypeWithAccInterestPots(state, pot.type, pot.productId));
    const siblingPots = useMemo(() => potsOfTheSameType.filter((siblingPot) => siblingPot.id !== pot.id), [pot.id, potsOfTheSameType]);
    const accountLabel = connectedBankAccount
        ? getAccountLabel(connectedBankAccount.name, connectedBankAccount.accountNumber)
        : undefined;
    const data = useMemo(() => {
        const items = [];
        const fastTransferItems = [];
        if (connectedAccount) {
            if (connectedAccount.pispBankId) {
                fastTransferItems.push({ itemType: 'openBanking', id: 'openBanking' });
            }
        }
        siblingPots.forEach((siblingPot) => {
            fastTransferItems.push({ itemType: 'pot', id: siblingPot.id, pot: siblingPot });
        });
        if (fastTransferItems.length) {
            items.push({ itemType: 'header', id: 'header-fast-transfers', title: strings.fastTransfers, first: true });
            items.push(...fastTransferItems);
        }
        if (connectedAccount) {
            items.push({
                itemType: 'header',
                id: 'header-other-transfer-type',
                title: strings.otherTransferType,
                first: items.length === 0,
            });
            items.push({ itemType: 'directDebit', id: 'directDebit' });
        }
        return items;
    }, [connectedAccount, siblingPots]);
    const handleSelect = useCallback((selection) => () => {
        onSelect(selection);
        ChooseAccountToDepositToPotModal.hide();
    }, [onSelect]);
    const renderItem = useCallback(({ item }) => {
        switch (item.itemType) {
            case 'header':
                return (<>
              <Spacer h={item.first ? 8 : 16}/>
              <SectionHeader horizontalMargins>{item.title}</SectionHeader>
              <Spacer h={8}/>
            </>);
            case 'pot':
                return (<TransferAccountRow name={item.pot.name} Icon={<EmojiText>{item.pot.emoji}</EmojiText>} selected={current?.type === 'pot' && current.pot.id === item.pot.id} onPress={handleSelect({ type: 'pot', pot: item.pot })} desc={<View style={styles.row}>
                  <Text TextThin-14 Secondary>
                    Available{' '}
                  </Text>
                  <Amount NumbersThin-14 Secondary value={item.pot?.withdrawableBalance ?? item.pot.balance}/>
                </View>}/>);
            case 'openBanking':
                return (<TransferAccountRow name={strings.openBankingTransfer} selected={current?.type === 'openBankingDeposit'} onPress={handleSelect({ type: 'openBankingDeposit' })} Icon={connectedAccount ? <LogoBankAccount size="R44" data={connectedAccount}/> : null} desc={<>
                  {!!accountLabel && (<Text TextThin-14 Secondary>
                      {accountLabel}
                    </Text>)}
                  <Text TextThin-14 Secondary>
                    {strings.openBankingTiming}
                  </Text>
                </>}/>);
            case 'directDebit':
            default:
                return (<TransferAccountRow name={strings.directDebit} selected={current?.type === 'singleDirectDebit'} onPress={handleSelect({ type: 'singleDirectDebit' })} Icon={connectedAccount ? <LogoBankAccount size="R44" data={connectedAccount}/> : null} desc={<>
                  {!!accountLabel && (<Text TextThin-14 Secondary>
                      {accountLabel}
                    </Text>)}
                  <Text TextThin-14 Secondary>
                    {strings.directDebitTiming}
                  </Text>
                </>}/>);
        }
    }, [accountLabel, connectedAccount, current, handleSelect]);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        sharedValue.value = handle.contentOffset.y;
    }, []);
    const contentContainerStyle = useMarginBottom('paddingBottom');
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    return (<ModalFlatList style={style} data={data} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle}/>);
};
export const ChooseAccountToDepositToPotModal = {
    show: (pot, current, onSelect, sharedValue, onDidHideCallback) => {
        Modal.show(<ChooseAccountToDepositToPotModalContent pot={pot} current={current} onSelect={onSelect} sharedValue={sharedValue}/>, {
            onDidHideCallback,
            title: strings.title,
            containerStyle: styles.modal,
            scrollable: {
                scrollValue: sharedValue,
            },
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});
